import type { FleetNode, LiveRead } from '../../lib/fleetNodes';

export const ROOMS_MAP_PLATFORMS = ['ut', 'ek21', 'wootalk', 'site', 'jd'] as const;

export type RoomsMapPlatform = (typeof ROOMS_MAP_PLATFORMS)[number];

export type UtMapRoom = {
  id: string;
  name: string;
  online: number;
  claimed: boolean;
  sessionId: string | null;
};

export type Ek21MapRoom = {
  id: string;
  online: number;
  real: number;
  empty: boolean;
  cooling: boolean;
  sessionId: string | null;
};

type SectionBase = {
  read: LiveRead;
  stale: boolean;
  nodeId: string | null;
};

export type RoomsMapSection =
  | (SectionBase & {
      platform: 'ut';
      claimed: number;
      uncovered: number;
      rooms: UtMapRoom[];
    })
  | (SectionBase & {
      platform: 'ek21';
      rooms: Ek21MapRoom[];
    })
  | (SectionBase & {
      platform: 'wootalk';
      matches: number;
      proxyDegraded: boolean;
    })
  | (SectionBase & {
      platform: 'site';
      state: 'up' | 'idle' | null;
    })
  | (SectionBase & {
      platform: 'jd';
      alert: boolean;
      alertReason: string;
    });

export function waterfallHash(sessionId: string): string {
  return `#/waterfall/${encodeURIComponent(sessionId)}`;
}

function isPlatform(value: unknown): value is RoomsMapPlatform {
  return ROOMS_MAP_PLATFORMS.some((platform) => platform === value);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function whole(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.trunc(value));
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function sessionIdOf(row: Record<string, unknown>): string | null {
  const id = text(row.sessionId);
  return id === '' ? null : id;
}

function seenMs(lastSeenAt: string): number {
  const sqlite = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/.exec(lastSeenAt);
  const seen = Date.parse(sqlite ? `${sqlite[1]}T${sqlite[2]}Z` : lastSeenAt);
  return Number.isFinite(seen) ? seen : Number.NEGATIVE_INFINITY;
}

function platformOf(node: FleetNode): RoomsMapPlatform | null {
  if (node.liveStatus === 'ok' && isPlatform(node.live.platform)) return node.live.platform;
  const keys = Object.keys(node.services).filter(isPlatform);
  const only = keys[0];
  return keys.length === 1 && only !== undefined ? only : null;
}

function prefer(current: FleetNode, next: FleetNode): FleetNode {
  if (current.stale !== next.stale) return current.stale ? next : current;
  return seenMs(next.lastSeenAt) > seenMs(current.lastSeenAt) ? next : current;
}

function utRooms(live: Record<string, unknown>): UtMapRoom[] {
  if (!Array.isArray(live.rooms)) return [];
  const rooms: UtMapRoom[] = [];
  const seen = new Set<string>();
  for (const row of live.rooms) {
    const record = asRecord(row);
    if (!record) continue;
    const id = text(record.id);
    if (id === '' || seen.has(id)) continue;
    seen.add(id);
    rooms.push({
      id,
      name: text(record.name),
      online: whole(record.online),
      claimed: record.claimed === true,
      sessionId: sessionIdOf(record),
    });
  }
  return rooms;
}

function ek21Rooms(live: Record<string, unknown>): Ek21MapRoom[] {
  if (!Array.isArray(live.rooms)) return [];
  const rooms: Ek21MapRoom[] = [];
  const seen = new Set<string>();
  for (const row of live.rooms) {
    const record = asRecord(row);
    if (!record) continue;
    const id = text(record.id);
    if (id === '' || seen.has(id)) continue;
    seen.add(id);
    const real = whole(record.real);
    rooms.push({
      id,
      online: whole(record.online),
      real,
      empty: record.empty === true || real <= 0,
      cooling: record.cooling === true,
      sessionId: sessionIdOf(record),
    });
  }
  return rooms;
}

function emptySection(platform: RoomsMapPlatform): RoomsMapSection {
  const base = { read: 'missing' as const, stale: false, nodeId: null };
  switch (platform) {
    case 'ut':
      return { ...base, platform, claimed: 0, uncovered: 0, rooms: [] };
    case 'ek21':
      return { ...base, platform, rooms: [] };
    case 'wootalk':
      return { ...base, platform, matches: 0, proxyDegraded: false };
    case 'site':
      return { ...base, platform, state: null };
    case 'jd':
      return { ...base, platform, alert: false, alertReason: '' };
    default: {
      const unreachable: never = platform;
      return unreachable;
    }
  }
}

function fill(platform: RoomsMapPlatform, node: FleetNode): RoomsMapSection {
  const base = { read: node.liveStatus, stale: node.stale, nodeId: node.nodeId };
  if (node.liveStatus !== 'ok') return { ...emptySection(platform), ...base, read: node.liveStatus };
  const live = node.live;
  switch (platform) {
    case 'ut': {
      const coverage = asRecord(live.coverage);
      return {
        ...base,
        platform,
        claimed: whole(coverage?.claimed),
        uncovered: whole(coverage?.uncovered),
        rooms: utRooms(live),
      };
    }
    case 'ek21':
      return { ...base, platform, rooms: ek21Rooms(live) };
    case 'wootalk':
      return {
        ...base,
        platform,
        matches: whole(live.matches),
        proxyDegraded: live.proxyDegraded === true,
      };
    case 'site':
      return {
        ...base,
        platform,
        state: live.state === 'up' || live.state === 'idle' ? live.state : null,
      };
    case 'jd': {
      const health = asRecord(live.health);
      const alert = health?.alert === true;
      return {
        ...base,
        platform,
        alert,
        alertReason: alert ? text(health?.alertReason) : '',
      };
    }
    default: {
      const unreachable: never = platform;
      return unreachable;
    }
  }
}

export function buildRoomsMap(nodes: readonly FleetNode[]): RoomsMapSection[] {
  const chosen = new Map<RoomsMapPlatform, FleetNode>();
  for (const node of nodes) {
    const platform = platformOf(node);
    if (platform === null) continue;
    const current = chosen.get(platform);
    chosen.set(platform, current === undefined ? node : prefer(current, node));
  }
  return ROOMS_MAP_PLATFORMS.map((platform) => {
    const node = chosen.get(platform);
    return node === undefined ? emptySection(platform) : fill(platform, node);
  });
}
